import { useEffect, useRef, useMemo, useState } from 'react';
import type { GeminiAudioManager } from '@/lib/gemini-audio';

export type AmbientState = 'disconnected' | 'connecting' | 'idle' | 'user-speaking' | 'agent-speaking';

interface AmbientWaveProps {
    state: AmbientState;
    className?: string;
    audioManager?: GeminiAudioManager | null;
}

// Lerp helper
const lerp = (start: number, end: number, factor: number) => {
    return start + (end - start) * factor;
};

// Color lerp helper
const lerpColor = (start: string, end: string, factor: number) => {
    const hex2rgb = (hex: string) => {
        const r = parseInt(hex.slice(1, 3), 16);
        const g = parseInt(hex.slice(3, 5), 16);
        const b = parseInt(hex.slice(5, 7), 16);
        return [r, g, b];
    };

    const [r1, g1, b1] = hex2rgb(start);
    const [r2, g2, b2] = hex2rgb(end);

    const r = Math.round(lerp(r1, r2, factor));
    const g = Math.round(lerp(g1, g2, factor));
    const b = Math.round(lerp(b1, b2, factor));

    return `#${((1 << 24) + (r << 16) + (g << 8) + b).toString(16).slice(1)}`;
};

const AmbientWave = (props: AmbientWaveProps) => {
    const { state, className = '' } = props;

    // Target configuration based on state
    const targetConfig = useMemo(() => {
        const baseAmplitude = 18;

        switch (state) {
            case 'disconnected':
                return {
                    speed: 0,
                    amplitude: 0,
                    modulation: 1,
                    type: 'traveling' as const,
                    wave1Opacity: 0,
                    wave2Opacity: 0,
                    wave1Color: '#22d3ee',
                    wave2Color: '#e879f9',
                };
            case 'connecting':
                return {
                    speed: 15,
                    amplitude: 40,
                    modulation: 1,
                    type: 'traveling' as const,
                    wave1Opacity: 0.8,
                    wave2Opacity: 0,
                    wave1Color: '#22d3ee',
                    wave2Color: '#e879f9',
                };
            case 'idle':
                return {
                    speed: 1.0,
                    amplitude: 38,
                    modulation: 1,
                    type: 'traveling' as const,
                    wave1Opacity: 0.5,
                    wave2Opacity: 0.4,
                    wave1Color: '#22d3ee',
                    wave2Color: '#e879f9',
                };
            case 'user-speaking':
                return {
                    speed: 1.5,
                    amplitude: baseAmplitude + 40,
                    modulation: 1.2,
                    type: 'standing' as const,
                    wave1Opacity: 0.9,
                    wave2Opacity: 0,
                    wave1Color: '#06b6d4',
                    wave2Color: '#e879f9',
                };
            case 'agent-speaking':
                return {
                    speed: 1.2,
                    amplitude: baseAmplitude + 30,
                    modulation: 1.0,
                    type: 'standing' as const,
                    wave1Opacity: 0.8,
                    wave2Opacity: 0,
                    wave1Color: '#e879f9',
                    wave2Color: '#06b6d4',
                };
            default:
                return {
                    speed: 0,
                    amplitude: 0,
                    modulation: 1,
                    type: 'traveling' as const,
                    wave1Opacity: 0,
                    wave2Opacity: 0,
                    wave1Color: '#22d3ee',
                    wave2Color: '#e879f9',
                };
        }
    }, [state]);

    // Current visual state (interpolated)
    const currentConfigRef = useRef(targetConfig);
    const modeMixRef = useRef(0); // 0 = Traveling, 1 = Standing

    // Renderable state (for the view)
    const [visualConfig, setVisualConfig] = useState(targetConfig);
    const [paths, setPaths] = useState({
        traveling: { w1: '', w2: '' },
        standing: { w1: '', w2: '' }
    });

    const animationRef = useRef<number>();
    const offsetRef = useRef(0);
    const lastSpokenRef = useRef(0);

    const generateWavePath = (
        amplitude: number,
        offset: number,
        frequency: number,
        modulation: number,
        type: 'traveling' | 'standing'
    ): string => {
        const height = 200;
        const width = 2400;
        const minHeight = 12;

        let path = `M0,${height} `;

        if (type === 'standing') {
            const time = offset * 0.05;

            for (let x = 0; x <= width; x += 15) {
                const normX = x / width;
                const cx = x - width / 2;

                const y1 = Math.sin(cx * 0.013 + time) * 0.5;
                const y2 = Math.sin(cx * 0.021 - time * 1.3) * 0.5;
                const y3 = Math.sin(cx * 0.047 + time * 0.7) * 0.3;
                const y4 = Math.sin(cx * 0.009 - time * 0.2) * 0.4;

                const envelope = Math.pow(Math.sin(normX * Math.PI), 0.5);
                const rawY = (y1 + y2 + y3 + y4) * amplitude * modulation * envelope;
                const y = height - minHeight - Math.abs(rawY);

                path += `L${x},${y} `;
            }
        } else {
            for (let x = 0; x <= width; x += 20) {
                const normX = x / width;
                // "Mountain" envelope: 0 at edges, 1 in center
                const envelope = Math.sin(normX * Math.PI);

                const osc =
                    (Math.sin((x + offset) * 0.005 * frequency) * amplitude * 1.5 +
                        Math.sin((x + offset) * 0.0025 * frequency) * amplitude * 0.5) * envelope;

                // Lift scaled by envelope to create the arch/mountain shape
                // Boosted by 1.3 in the middle as requested
                const lift = (amplitude * 1.8 * envelope * 1.3) + minHeight;
                const y = height - lift - osc;

                path += `L${x},${y} `;
            }
        }

        path += `L${width},${height} L0,${height} Z`;
        return path;
    };

    useEffect(() => {
        // Reset current config if it drifts too far or on mount? 
        // No, we want to maintain state for continuity.
        // But if we switch drastically, validation might be needed.
    }, []);

    useEffect(() => {
        const animate = () => {
            // 1. Calculate Target (Base + Audio Modifiers)
            let effectiveTarget = { ...targetConfig };
            let audioAmplitudeBoost = 0;

            if (props.audioManager) {
                // Agent Output
                if (props.audioManager.outputAnalyser) {
                    const dataArray = new Uint8Array(props.audioManager.outputAnalyser.frequencyBinCount);
                    props.audioManager.outputAnalyser.getByteFrequencyData(dataArray);

                    const checkBinCount = Math.min(dataArray.length, 50);
                    let sum = 0;
                    for (let i = 0; i < checkBinCount; i++) sum += dataArray[i];
                    const avg = sum / checkBinCount;

                    // Hysteresis: Update lastSpoken if we detect significant audio
                    if (avg > 5) {
                        lastSpokenRef.current = Date.now();
                        audioAmplitudeBoost = (avg / 128.0) * 40;
                    }

                    // Check if we are within the "hold" window (300ms)
                    const isSpeaking = (Date.now() - lastSpokenRef.current) < 300;

                    if (isSpeaking) {
                        // Dynamically switch to 'agent-speaking' visuals if hearing audio (or recently heard it)
                        effectiveTarget = {
                            ...effectiveTarget,
                            speed: 1.2,
                            amplitude: 18 + 30,
                            type: 'standing', // Stick to standing wave during speech
                            wave1Opacity: 0.8,
                            wave1Color: '#e879f9',
                            wave2Color: '#06b6d4',
                        };

                        // If we are in the hold period but audio is low, decay the boost smoothly
                        if (avg <= 5) {
                            audioAmplitudeBoost = 0; // The lerp will smooth this down
                        }
                    }
                }

                // User Input
                if (state === 'user-speaking' && props.audioManager.inputAnalyser) {
                    const dataArray = new Uint8Array(props.audioManager.inputAnalyser.frequencyBinCount);
                    props.audioManager.inputAnalyser.getByteFrequencyData(dataArray);

                    let sum = 0;
                    for (let i = 0; i < dataArray.length; i++) sum += dataArray[i];
                    const avg = sum / dataArray.length;

                    audioAmplitudeBoost = (avg / 128.0) * 40;
                }
            }

            // 2. Interpolate Current State towards Target
            // Factor: 0.1 for smooth ease-out (increased smoothing slightly if needed, but 0.1 is responsive)
            const factor = 0.1;
            const current = currentConfigRef.current;

            // Determine mixture target (Standing = 1, Traveling = 0)
            const targetMix = effectiveTarget.type === 'standing' ? 1 : 0;
            // Smoothly interpolate the mix
            const nextMix = lerp(modeMixRef.current, targetMix, 0.05); // Slower transition for shape blend
            modeMixRef.current = nextMix;

            const nextConfig = {
                speed: lerp(current.speed, effectiveTarget.speed, factor),
                amplitude: lerp(current.amplitude, effectiveTarget.amplitude + audioAmplitudeBoost, factor),
                modulation: lerp(current.modulation, effectiveTarget.modulation, factor),
                type: effectiveTarget.type, // Logic type flips, but visual mix handles the blend
                wave1Opacity: lerp(current.wave1Opacity, effectiveTarget.wave1Opacity, factor),
                wave2Opacity: lerp(current.wave2Opacity, effectiveTarget.wave2Opacity, factor),
                wave1Color: lerpColor(current.wave1Color, effectiveTarget.wave1Color, 0.05), // Slower color transition
                wave2Color: lerpColor(current.wave2Color, effectiveTarget.wave2Color, 0.05),
            };

            currentConfigRef.current = nextConfig;

            // 3. Update Animation Logic
            offsetRef.current += nextConfig.speed;

            // Calculate paths for BOTH types if they are visible
            let newPaths = { ...paths };
            const mix = nextMix;

            // Traveling Paths (visible if mix < 1)
            if (mix < 0.99) {
                newPaths.traveling = {
                    w1: generateWavePath(nextConfig.amplitude, offsetRef.current, 1, nextConfig.modulation, 'traveling'),
                    w2: generateWavePath(nextConfig.amplitude * 0.8, offsetRef.current + 200, 1.2, nextConfig.modulation, 'traveling')
                };
            }

            // Standing Paths (visible if mix > 0)
            if (mix > 0.01) {
                newPaths.standing = {
                    w1: generateWavePath(nextConfig.amplitude, offsetRef.current, 1, nextConfig.modulation, 'standing'),
                    w2: generateWavePath(nextConfig.amplitude * 0.8, offsetRef.current + 200, 1.2, nextConfig.modulation, 'standing')
                };
            }

            // 4. Render Updates
            setPaths(newPaths);
            setVisualConfig(nextConfig);

            animationRef.current = requestAnimationFrame(animate);
        };

        animate();

        return () => {
            if (animationRef.current) {
                cancelAnimationFrame(animationRef.current);
            }
        };
    }, [targetConfig, props.audioManager, state]);

    const mix = modeMixRef.current;
    // Opacity helpers - multiplying by mix weighting
    // Traveling: 1 - mix
    // Standing: mix
    const travelingOp1 = visualConfig.wave1Opacity * (1 - mix);
    const travelingOp2 = visualConfig.wave2Opacity * (1 - mix);
    const standingOp1 = visualConfig.wave1Opacity * mix;
    const standingOp2 = visualConfig.wave2Opacity * mix;

    return (
        <div
            className={`absolute bottom-0 left-0 right-0 pointer-events-none overflow-visible transition-opacity duration-800 ${className}`}
            style={{
                height: '200px',
                maskImage: 'linear-gradient(to top, black 40%, transparent 100%)',
                WebkitMaskImage: 'linear-gradient(to top, black 40%, transparent 100%)',
            }}
        >
            {/* 
               We render BOTH sets overlaid. 
               The opacity handles the crossfade.
            */}

            {/* TRAVELING WAVE SET (Idle/Default) */}
            <div style={{ opacity: 1 - mix, transition: 'none' }}>
                <svg
                    className="absolute bottom-0 left-0 h-[160px]"
                    style={{
                        width: '100%',
                        opacity: visualConfig.wave1Opacity, // Base opacity, parent div handles mix fade? No, parent div complicates mask. Let's do direct opacity.
                        visibility: mix >= 0.99 ? 'hidden' : 'visible',
                        filter: `drop-shadow(0 0 15px ${visualConfig.wave1Color})`,
                    }}
                    viewBox="0 0 2400 200"
                    preserveAspectRatio="none"
                >
                    <defs>
                        <linearGradient id="beam-travel-1" x1="0%" y1="100%" x2="0%" y2="0%">
                            <stop offset="0%" stopColor={visualConfig.wave1Color} stopOpacity="0.6" />
                            <stop offset="100%" stopColor={visualConfig.wave1Color} stopOpacity="0" />
                        </linearGradient>
                    </defs>
                    <path d={paths.traveling.w1} fill="url(#beam-travel-1)" />
                </svg>
                <svg
                    className="absolute bottom-0 left-0 h-[160px]"
                    style={{
                        width: '100%',
                        opacity: visualConfig.wave2Opacity,
                        visibility: mix >= 0.99 ? 'hidden' : 'visible',
                        filter: `drop-shadow(0 0 15px ${visualConfig.wave2Color})`,
                        mixBlendMode: 'screen',
                    }}
                    viewBox="0 0 2400 200"
                    preserveAspectRatio="none"
                >
                    <defs>
                        <linearGradient id="beam-travel-2" x1="0%" y1="100%" x2="0%" y2="0%">
                            <stop offset="0%" stopColor={visualConfig.wave2Color} stopOpacity="0.5" />
                            <stop offset="100%" stopColor={visualConfig.wave2Color} stopOpacity="0" />
                        </linearGradient>
                    </defs>
                    <path d={paths.traveling.w2} fill="url(#beam-travel-2)" />
                </svg>
            </div>

            {/* STANDING WAVE SET (Speaking) */}
            <div style={{ opacity: mix, transition: 'none' }}>
                <svg
                    className="absolute bottom-0 left-0 h-[160px]"
                    style={{
                        width: '100%',
                        opacity: visualConfig.wave1Opacity,
                        visibility: mix <= 0.01 ? 'hidden' : 'visible',
                        filter: `drop-shadow(0 0 15px ${visualConfig.wave1Color})`,
                    }}
                    viewBox="0 0 2400 200"
                    preserveAspectRatio="none"
                >
                    <defs>
                        <linearGradient id="beam-std-1" x1="0%" y1="100%" x2="0%" y2="0%">
                            <stop offset="0%" stopColor={visualConfig.wave1Color} stopOpacity="0.6" />
                            <stop offset="100%" stopColor={visualConfig.wave1Color} stopOpacity="0" />
                        </linearGradient>
                    </defs>
                    <path d={paths.standing.w1} fill="url(#beam-std-1)" />
                </svg>
                <svg
                    className="absolute bottom-0 left-0 h-[160px]"
                    style={{
                        width: '100%',
                        opacity: visualConfig.wave2Opacity,
                        visibility: mix <= 0.01 ? 'hidden' : 'visible',
                        filter: `drop-shadow(0 0 15px ${visualConfig.wave2Color})`,
                        mixBlendMode: 'screen',
                    }}
                    viewBox="0 0 2400 200"
                    preserveAspectRatio="none"
                >
                    <defs>
                        <linearGradient id="beam-std-2" x1="0%" y1="100%" x2="0%" y2="0%">
                            <stop offset="0%" stopColor={visualConfig.wave2Color} stopOpacity="0.5" />
                            <stop offset="100%" stopColor={visualConfig.wave2Color} stopOpacity="0" />
                        </linearGradient>
                    </defs>
                    <path d={paths.standing.w2} fill="url(#beam-std-2)" />
                </svg>
            </div>
        </div>
    );
};

export default AmbientWave;
